import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Types for filter values - should align with filters state in Proposals.tsx
export interface ProposalFilterValues {
    status: string[];
    heatLevel: string[];
    client: string;
    manager: string;
}

interface ProposalFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentFilters: ProposalFilterValues;
    onApplyFilters: (newFilters: ProposalFilterValues) => void;
    onClearFilters: () => void;
    // Add other necessary props like clients list if we want a dropdown later
}

const ALL_STATUSES = [
    { id: 'negotiating', name: 'Em Negociação' },
    { id: 'won', name: 'Ganha' },
    { id: 'lost', name: 'Perdida' },
    { id: 'cancelled', name: 'Cancelada' },
];

const ALL_HEAT_LEVELS = [
    { id: 'frozen', name: 'Congelado' },
    { id: 'cold', name: 'Frio' },
    { id: 'cool', name: 'Ok' },
    { id: 'warm', name: 'Quente' },
    { id: 'hot', name: 'Muito Quente' },
    { id: 'burning', name: 'Pegando Fogo' },
];

export default function ProposalFilterModal({
    isOpen,
    onClose,
    currentFilters,
    onApplyFilters,
    onClearFilters,
}: ProposalFilterModalProps) {
    const [internalFilters, setInternalFilters] = useState<ProposalFilterValues>(currentFilters);

    useEffect(() => {
        setInternalFilters(currentFilters);
    }, [currentFilters, isOpen]); // Reset internal state when currentFilters change or modal opens

    const handleStatusChange = (statusId: string) => {
        setInternalFilters(prev => ({
            ...prev,
            status: prev.status.includes(statusId)
                ? prev.status.filter(s => s !== statusId)
                : [...prev.status, statusId],
        }));
    };

    const handleHeatLevelChange = (levelId: string) => {
        setInternalFilters(prev => ({
            ...prev,
            heatLevel: prev.heatLevel.includes(levelId)
                ? prev.heatLevel.filter(hl => hl !== levelId)
                : [...prev.heatLevel, levelId],
        }));
    };

    const handleApply = () => {
        onApplyFilters(internalFilters);
        onClose();
    };

    const handleClear = () => {
        const cleared: ProposalFilterValues = { status: [], heatLevel: [], client: '', manager: '' };
        setInternalFilters(cleared);
        onClearFilters(); // This will clear filters in parent and cause re-render with cleared filters
        // onClose(); // Optionally close modal on clear, or let user see cleared filters
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div>
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        Filtrar Propostas
                                    </Dialog.Title>
                                    <div className="mt-5 space-y-6">
                                        {/* Status Filter */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Status</label>
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                {ALL_STATUSES.map(status => (
                                                    <label key={status.id} className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            checked={internalFilters.status.includes(status.id)}
                                                            onChange={() => handleStatusChange(status.id)}
                                                        />
                                                        <span>{status.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Heat Level Filter */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Termômetro (Heat Level)</label>
                                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                {ALL_HEAT_LEVELS.map(level => (
                                                    <label key={level.id} className="flex items-center space-x-2">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                            checked={internalFilters.heatLevel.includes(level.id)}
                                                            onChange={() => handleHeatLevelChange(level.id)}
                                                        />
                                                        <span>{level.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Client Filter */}
                                        <div>
                                            <label htmlFor="filter-client" className="block text-sm font-medium text-gray-700">Nome do Cliente</label>
                                            <input
                                                type="text"
                                                id="filter-client"
                                                value={internalFilters.client}
                                                onChange={(e) => setInternalFilters(prev => ({ ...prev, client: e.target.value }))}
                                                className="input-primary mt-1 w-full"
                                                placeholder="Digite o nome do cliente"
                                            />
                                        </div>

                                        {/* Manager Filter */}
                                        <div>
                                            <label htmlFor="filter-manager" className="block text-sm font-medium text-gray-700">Nome do Responsável</label>
                                            <input
                                                type="text"
                                                id="filter-manager"
                                                value={internalFilters.manager}
                                                onChange={(e) => setInternalFilters(prev => ({ ...prev, manager: e.target.value }))}
                                                className="input-primary mt-1 w-full"
                                                placeholder="Digite o nome do responsável"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-8 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        className="btn-primary w-full sm:ml-3 sm:w-auto"
                                        onClick={handleApply}
                                    >
                                        Aplicar Filtros
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
                                        onClick={handleClear}
                                    >
                                        Limpar Filtros
                                    </button>
                                    <button // Added explicit cancel button for clarity on mobile
                                        type="button"
                                        className="btn-secondary mt-3 w-full sm:mt-0 sm:hidden" // Show only on small screens to supplement X
                                        onClick={onClose}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
} 